import classNames from 'classnames'
import { format } from 'date-fns'
import React, { useState } from 'react'
import CountCard from '../../components/cards/CountCard'
import { DateRangePicker } from '../../components/inputs/dateRange'
import Dropdown from '../../components/inputs/dropdown'
import { PageHeader } from '../../components/typography/PageHeader'
import { CountCardContainer } from '../../components/wrapper/CountCardsContainer'
import { formatCurrency, parseDate, prettyFormatNumber } from '../../helpers'
import Layout from '../../layout'
import {
  useGetCreatorsAnalyticsQuery,
  useGetDashboardGMVReportQuery,
  useGetDashboardReportQuery,
  useGetRevenueAnalyticsQuery,
} from '../../redux-toolkits/reports/reports.slice'
import {
  DashboardReportParams,
  ReportPeriodRange,
} from '../../redux-toolkits/reports/reports.type'

import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { ChartCard } from '../../components/cards/ChartCard'
import { ProgressBar } from '../../components/progressBar'
import { TabNav } from '../../components/nav/tabNav'
import { PerformanceTable } from './views/performanceTable'
import { useSearchParams } from 'react-router-dom'

type DashboardReportRange = ReportPeriodRange | 'custom'

const periods: {
  name: string
  id: DashboardReportRange
}[] = [
  {
    name: 'Today',
    id: 'today',
  },
  {
    name: 'This week',
    id: 'this_week',
  },
  {
    name: 'MTD',
    id: 'this_month',
  },
  {
    name: 'YTD',
    id: 'this_year',
  },
  {
    name: 'ITD',
    id: 'inception_to_date',
  },
]

export const DashboardPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams()

  const [dashboardReportQuery, setDashboardReportQuery] =
    useState<DashboardReportParams>({
      filter: (searchParams.get('filter') as any) ?? 'today',
      fulfilmentCentreId: undefined,
      startDate: undefined,
      endDate: undefined,
    })

  const { data: dashboardReport, isFetching: loadingDashboardReport } =
    useGetDashboardReportQuery(
      { ...dashboardReportQuery },
      {
        refetchOnMountOrArgChange: true,
      },
    )

  const { data: gmv, isFetching: loadingDashboardGMVReport } =
    useGetDashboardGMVReportQuery(
      { ...dashboardReportQuery },
      {
        refetchOnMountOrArgChange: true,
      },
    )

  const { data: revenueAnalytics } = useGetRevenueAnalyticsQuery(
    { ...dashboardReportQuery },
    {
      refetchOnMountOrArgChange: true,
    },
  )
  const { data: creatorAnalytics } = useGetCreatorsAnalyticsQuery(
    { ...dashboardReportQuery },
    {
      refetchOnMountOrArgChange: true,
    },
  )

  return (
    <Layout>
      <div className="flex flex-col gap-10 w-full">
        {/* Header  */}
        <div className="flex items-center gap-4 flex-wrap justify-between">
          <PageHeader
            title="How Things Are Going"
            className="min-w-fit !mb-0"
          />

          <div className="flex items-center">
            <TabNav
              activeTab={dashboardReportQuery.filter ?? 'custom'}
              setActiveTab={(selected) => {
                const filter = selected as DashboardReportRange

                if (filter !== 'custom') {
                  setSearchParams({
                    filter: encodeURIComponent(filter),
                    page: encodeURIComponent(1),
                  })

                  setDashboardReportQuery({
                    ...dashboardReportQuery,
                    filter: filter,
                    isCustomDateRange: false,
                    endDate: undefined,
                    startDate: undefined,
                  })
                }
              }}
              tabs={periods}
              type="switcher-outline"
              size="small"
            />

            <Dropdown
              menuButton={
                <p
                  className={classNames(
                    'text-xs border py-[10px] px-3 rounded-md font-medium',
                    {
                      'text-sec-black border-transparent ':
                        !dashboardReportQuery.isCustomDateRange,
                      'text-primary border-primary':
                        dashboardReportQuery.isCustomDateRange,
                    },
                  )}
                >
                  {dashboardReportQuery.isCustomDateRange
                    ? `${format(
                        dashboardReportQuery.startDate ?? '',
                        'do MMM',
                      )} - ${format(
                        dashboardReportQuery.endDate ?? '',
                        'do MMM',
                      )}`
                    : 'Custom'}
                </p>
              }
              menuClassName="!min-w-max !border-[0px]"
              customMenuItems={
                <DateRangePicker
                  required
                  navLayout="around"
                  animate
                  mode="range"
                  selected={{
                    from:
                      parseDate(dashboardReportQuery.startDate)?.toDate() ??
                      undefined,
                    to:
                      parseDate(dashboardReportQuery.endDate)?.toDate() ??
                      undefined,
                  }}
                  onSelect={(selected) => {
                    if (selected?.from && selected?.to) {
                      setSearchParams({
                        page: encodeURIComponent(1),
                        startDate: encodeURIComponent(
                          selected?.from
                            ? format(selected?.from, 'yyyy-MM-dd')
                            : '',
                        ),
                        endDate: encodeURIComponent(
                          selected?.to
                            ? format(selected?.to, 'yyyy-MM-dd')
                            : '',
                        ),
                      })
                      setDashboardReportQuery({
                        ...dashboardReportQuery,
                        filter: undefined,
                        startDate: selected?.from
                          ? format(selected?.from, 'yyyy-MM-dd')
                          : undefined,
                        endDate: selected?.to
                          ? format(selected?.to, 'yyyy-MM-dd')
                          : undefined,
                        isCustomDateRange: true,
                      })
                    }
                  }}
                  numberOfMonths={2}
                  disabled={{ after: new Date() }}
                />
              }
            />
          </div>
        </div>

        {/* Cards  */}
        <div className="w-full flex flex-col gap-6">
          <CountCardContainer className="!my-0">
            <CountCard
              count={gmv?.data?.totalGMV}
              text="Gross Merchandise Value"
              isCurrency
              loading={loadingDashboardReport}
            >
              {`Total order count: ${gmv?.data?.orderCount}`}
            </CountCard>

            <CountCard
              count={dashboardReport?.data?.totalPayout}
              text="Total Revenue"
              isCurrency
              loading={loadingDashboardReport}
            />

            <CountCard
              count={dashboardReport?.data?.assignedFCs}
              text="Total Fulfilment Centre"
              isCurrency={false}
              loading={loadingDashboardReport}
            />

            <CountCard
              count={creatorAnalytics?.data?.totalVendors}
              text="Total Creators"
              isCurrency={false}
              loading={loadingDashboardReport}
            />
          </CountCardContainer>

          <div className="grid grid-cols-1 md:grid-cols-2  gap-6 ">
            <ChartCard
              title="Revenue Channels"
              value={formatCurrency(
                revenueAnalytics?.data?.byFulfilmentCentre?.reduce(
                  (acc, d) => acc + d.revenue,
                  0,
                ) ?? 0,
              )}
            >
              {revenueAnalytics?.data?.byFulfilmentCentre?.map((pm, idx) => {
                const total =
                  revenueAnalytics?.data?.byFulfilmentCentre?.reduce(
                    (acc, d) => acc + d.revenue,
                    0,
                  )
                return (
                  <div
                    className="flex flex-col gap-2 py-4 border-b border-divider-gray last:border-b-0 last:pb-0"
                    key={`${idx}`}
                  >
                    <p>{pm.fulfilmentCentreName}</p>

                    <p className="text-default-gray">
                      {formatCurrency(pm?.revenue ?? 0)}
                    </p>

                    <ProgressBar
                      step={pm?.revenue ?? 0}
                      total={total}
                      singleBarClassName={
                        ['!bg-lime-green', '!bg-red-650', '!bg-amber'][idx]
                      }
                      showPercent
                    />
                  </div>
                )
              })}
            </ChartCard>
          </div>

          <ChartCard title="Revenue Analytics" value={undefined}>
            <ResponsiveContainer width={'100%'} height={240} className={'mt-6'}>
              <BarChart
                data={
                  (revenueAnalytics?.data?.trend.length ?? 0) > 0
                    ? revenueAnalytics?.data?.trend
                        .filter((item) => item.value > 0)
                        .map((entry) => {
                          return {
                            label: entry.label,
                            revenue: entry.value,
                          }
                        })
                    : [{ label: '', revenue: 0 }]
                }
                margin={{
                  top: 0,
                  right: 0,
                  left: 0,
                  bottom: 12,
                }}
              >
                <CartesianGrid
                  strokeDasharray="3 0"
                  vertical={true}
                  stroke="#EAEAEA"
                />

                <YAxis
                  dataKey="revenue"
                  axisLine={false}
                  tickLine={false}
                  type="number"
                  style={{
                    fontSize: '12px',
                    fontWeight: 'normal',
                    color: '#555555',
                  }}
                  tickFormatter={(val) => prettyFormatNumber(val)}
                />

                <XAxis
                  dataKey="label"
                  axisLine={false}
                  tickLine={false}
                  angle={-45}
                  interval={0}
                  textAnchor="end"
                  style={{
                    fontSize: '12px',
                    fontWeight: 'normal',
                    color: '#555555',
                  }}
                  // tickFormatter={(val) => val.slice(0, 8) + '...'}
                />

                <Tooltip
                  content={({ payload }) => {
                    if (!payload?.length) return null
                    const dateLabel = payload[0]?.payload?.label
                    const revenue = payload[0]?.payload?.revenue

                    return (
                      <div className="flex flex-col gap-2 rounded-[8px] bg-[#F3F3F3] p-2 text-center text-xs max-w-[150px]">
                        <p className="text-sec-black">{dateLabel}</p>
                        <p className="font-bold text-base text-black">
                          {payload ? `${formatCurrency(revenue ?? 0, {})}` : ''}
                        </p>
                      </div>
                    )
                  }}
                  cursor={{
                    stroke: '#F45D2C',
                    strokeWidth: 1,
                    strokeDasharray: '8 8',
                    fill: 'transparent',
                  }}
                />

                <Bar
                  type="linear"
                  dataKey="revenue"
                  stroke={undefined}
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="#F45D2C"
                  maxBarSize={24}
                  radius={[6, 6, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <PerformanceTable
            mainDashQuery={dashboardReportQuery}
            searchParams={searchParams}
            setSearchParams={setSearchParams}
          />
        </div>
      </div>
    </Layout>
  )
}
